/**
 * Performance test data files (CSV / Excel): rows of test data a scenario's users go through, one row per journey
 * (iteration). Columns are template names ({{USERNAME}}, {{PASSWORD}}); a row's values replace the project's test data
 * with the same name for that journey. Stored encrypted (PerfDataSet), never sent back to the browser; the agent gets
 * the rows in a private file. docs/architecture.md "Performance test data files".
 */
import { z } from 'zod';
import { scriptVariables, type DataWhenUsedUp, type PerfScript } from './perf.js';

/** Limits of one file: enough for big credential lists, small enough for the agent's memory and the run job. */
export const DATA_FILE_LIMITS = { columns: 50, rows: 100_000, cellChars: 2000, bytes: 8 * 1024 * 1024 } as const;

/** A column is a template name: letters, digits and _ (not starting with a digit or `$`, which marks generators). */
export const DATA_COLUMN_RE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

export const perfDataSetInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    columns: z.array(z.string().regex(DATA_COLUMN_RE, 'Column names may hold letters, digits and _ only, and start with a letter')).min(1).max(DATA_FILE_LIMITS.columns),
    /** Columns whose values are secret (passwords): masked in check results, never shown again. */
    secretColumns: z.array(z.string()).max(DATA_FILE_LIMITS.columns),
    rows: z.array(z.array(z.string().max(DATA_FILE_LIMITS.cellChars))).min(1, 'The file has no rows under its header').max(DATA_FILE_LIMITS.rows),
  })
  .superRefine((d, ctx) => {
    if (new Set(d.columns.map((c) => c.toLowerCase())).size !== d.columns.length) ctx.addIssue({ code: 'custom', path: ['columns'], message: 'Two columns have the same name' });
    for (const c of d.secretColumns) if (!d.columns.includes(c)) ctx.addIssue({ code: 'custom', path: ['secretColumns'], message: `${c} is not a column` });
    const bad = d.rows.findIndex((r) => r.length !== d.columns.length);
    if (bad !== -1) ctx.addIssue({ code: 'custom', path: ['rows', bad], message: `Row ${bad + 1} has ${d.rows[bad]!.length} values, not ${d.columns.length}` });
  });
export type PerfDataSetInput = z.infer<typeof perfDataSetInputSchema>;

/** A data file as the API lists it: never the rows (secret columns), only non-secret values of the first rows. */
export interface PerfDataSetView {
  id: string;
  name: string;
  columns: string[];
  secretColumns: string[];
  rowCount: number;
  /** The first rows, secret columns as ••••••. */
  preview: string[][];
  createdBy: { id: string; name: string } | null;
  updatedAt: string;
  /** Performance tests whose scenarios use it. */
  usedBy: { id: string; name: string }[];
}

/** The data file of one scenario in a run (rows travel apart, in the job's `rows`). */
export interface RunScenarioData {
  name: string;
  columns: string[];
  secretColumns: string[];
  rows: number;
  whenUsedUp: DataWhenUsedUp;
}

/** Secret-looking column names, marked secret when a file is read (people can change it). */
export const looksSecret = (column: string) => /pass(word)?|pwd|secret|token|pin|otp|api_?key/i.test(column);

/** The value written after `|` in a template ({{USERNAME|demo@shop.test}}), per name: a sample row for templates. */
function writtenValues(script: PerfScript): Map<string, string> {
  const out = new Map<string, string>();
  const texts = JSON.stringify(script.transactions);
  for (const m of texts.matchAll(/\{\{\s*([A-Za-z_][A-Za-z0-9_]*)(?::(?:url|form|json))?\s*\|([^{}]*?)\}\}/g)) {
    if (out.has(m[1]!)) continue;
    try {
      out.set(m[1]!, JSON.parse(`"${m[2]!}"`) as string);
    } catch {
      out.set(m[1]!, m[2]!);
    }
  }
  return out;
}

/**
 * The CSV template of a script: one column per test data name the script uses (not APP_URL, not values taken from
 * responses) and an example row with the written values (secrets left empty). Download, fill in, upload.
 */
export function dataTemplate(script: PerfScript): { columns: string[]; secretColumns: string[]; csv: string } {
  const vars = scriptVariables(script);
  const columns = [...new Set([...vars.data, ...script.secretKeys])].filter((k) => k !== 'APP_URL' && DATA_COLUMN_RE.test(k));
  const secretColumns = columns.filter((c) => script.secretKeys.includes(c));
  const written = writtenValues(script);
  const example = columns.map((c) => (secretColumns.includes(c) ? '' : (written.get(c) ?? '')));
  return { columns, secretColumns, csv: toCsv([columns, ...(example.some(Boolean) ? [example] : [])]) };
}

const csvCell = (v: string) => (/[",\r\n]/.test(v) || /^\s|\s$/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
export const toCsv = (rows: string[][]) => rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';

/**
 * Reads CSV (RFC 4180: quotes, "" inside quotes, line breaks in quoted cells; `,` or `;` or tab, whichever the header
 * line uses most). Empty lines are skipped. A leading byte order mark is ignored.
 */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const firstLine = src.slice(0, src.search(/\r?\n|$/));
  const sep = [',', ';', '\t'].map((s) => [s, firstLine.split(s).length] as const).sort((a, b) => b[1] - a[1])[0]![0];
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') (cell += '"'), i++;
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === sep) row.push(cell), (cell = '');
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      if (row.some((c) => c !== '')) rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c !== '')) rows.push(row);
  return rows;
}

/**
 * A table read from a file (header row + rows) as a data file: header cells trimmed, rows padded / cut to the header,
 * secret-looking columns marked. Errors in plain words.
 */
export function tableToDataSet(name: string, table: string[][]): { ok: true; data: PerfDataSetInput } | { ok: false; error: string } {
  const [header, ...rest] = table;
  if (!header?.length) return { ok: false, error: 'The file is empty: put the column names (for example USERNAME, PASSWORD) in the first row.' };
  const columns = header.map((h) => h.trim());
  const bad = columns.find((c) => !DATA_COLUMN_RE.test(c));
  if (bad !== undefined) return { ok: false, error: `“${bad || '(empty)'}” can’t be a column name: use the test data names of the script (letters, digits and _, like USERNAME).` };
  const rows = rest.map((r) => columns.map((_, i) => r[i] ?? ''));
  if (!rows.length) return { ok: false, error: 'The file has a header but no rows: add one row per journey under it.' };
  if (rows.length > DATA_FILE_LIMITS.rows) return { ok: false, error: `The file has ${rows.length} rows; up to ${DATA_FILE_LIMITS.rows} are allowed.` };
  const parsed = perfDataSetInputSchema.safeParse({ name, columns, secretColumns: columns.filter(looksSecret), rows });
  return parsed.success ? { ok: true, data: parsed.data } : { ok: false, error: parsed.error.issues[0]!.message };
}

/** "1.10.0" >= "1.2.0": numeric parts compared in order (missing parts are 0). */
export function versionAtLeast(version: string | null | undefined, min: string): boolean {
  if (!version) return false;
  const a = version.split(/[.+-]/).map((x) => Number.parseInt(x, 10) || 0);
  const b = min.split('.').map((x) => Number.parseInt(x, 10) || 0);
  for (let i = 0; i < b.length; i++) if ((a[i] ?? 0) !== b[i]) return (a[i] ?? 0) > b[i]!;
  return true;
}

/** Load agents older than this ignore data files, so runs that use one never go to them. */
export const LOAD_AGENT_DATA_MIN_VERSION = '1.2.0';
